import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import { Layout } from "@/components/Layout";
import { TierGate } from "@/components/TierGate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  Plus, Trash2, Swords, SkipForward, Heart, Shield, ChevronDown,
  UserPlus, Skull, Users, X, Flag, Eye, Zap, BookOpen, Target
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONDITIONS = [
  "Blinded", "Charmed", "Deafened", "Frightened", "Grappled",
  "Incapacitated", "Invisible", "Paralyzed", "Petrified", "Poisoned",
  "Prone", "Restrained", "Stunned", "Unconscious", "Exhaustion",
  "Concentrating"
];

const CONDITION_COLORS = {
  Blinded: "bg-gray-600", Charmed: "bg-pink-700", Deafened: "bg-gray-500",
  Frightened: "bg-purple-700", Grappled: "bg-yellow-700", Incapacitated: "bg-red-800",
  Invisible: "bg-blue-800", Paralyzed: "bg-orange-700", Petrified: "bg-stone-600",
  Poisoned: "bg-green-700", Prone: "bg-amber-700", Restrained: "bg-yellow-800",
  Stunned: "bg-indigo-700", Unconscious: "bg-red-900", Exhaustion: "bg-orange-800",
  Concentrating: "bg-cyan-700"
};

const parseFirstNumber = (str, fallback = 10) => {
  if (!str) return fallback;
  const match = String(str).match(/(\d+)/);
  return match ? parseInt(match[1], 10) : fallback;
};

const abilityMod = (score) => {
  const mod = Math.floor((score - 10) / 2);
  return mod >= 0 ? `+${mod}` : `${mod}`;
};

const CombatTrackerPage = () => {
  const { campaignId } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [encounters, setEncounters] = useState([]);
  const [activeEncounter, setActiveEncounter] = useState(null);
  const [characters, setCharacters] = useState([]);
  const [creatures, setCreatures] = useState([]);
  const [npcs, setNpcs] = useState([]);
  const [loading, setLoading] = useState(true);
  const confirm = useConfirm();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newEncounterName, setNewEncounterName] = useState("");
  const [dmgInputs, setDmgInputs] = useState({});
  const [newCombatant, setNewCombatant] = useState({
    name: "", initiative: 10, hp: 10, max_hp: 10, ac: 10, is_player: false,
    source_id: "", source_type: ""
  });
  const [conditionDropdown, setConditionDropdown] = useState(null);
  const [selectedCombatant, setSelectedCombatant] = useState(null);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [selectedEntityType, setSelectedEntityType] = useState("");
  const [addSearch, setAddSearch] = useState("");

  useEffect(() => { fetchData(); }, [campaignId]);

  const fetchData = async () => {
    try {
      const [campRes, encRes, charRes, creatRes, npcRes] = await Promise.all([
        axios.get(`${API}/campaigns/${campaignId}`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/encounters`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/characters`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/creatures`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/npcs`, { withCredentials: true })
      ]);
      setCampaign(campRes.data);
      setEncounters(encRes.data);
      setCharacters(charRes.data);
      setCreatures(creatRes.data);
      setNpcs(npcRes.data);
      if (encRes.data.length > 0 && !activeEncounter) {
        setActiveEncounter(encRes.data[0]);
      }
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const refreshEncounter = async (encId) => {
    try {
      const res = await axios.get(`${API}/encounters/${encId}`, { withCredentials: true });
      setActiveEncounter(res.data);
      setEncounters(prev => prev.map(e => e.id === encId ? res.data : e));
      if (selectedCombatant) {
        const updated = res.data.combatants?.find(c => c.id === selectedCombatant.id);
        if (updated) setSelectedCombatant(updated);
      }
    } catch { /* ignore */ }
  };

  const createEncounter = async () => {
    if (!newEncounterName.trim()) return;
    try {
      const res = await axios.post(`${API}/encounters`, {
        campaign_id: campaignId, name: newEncounterName
      }, { withCredentials: true });
      toast.success("Encounter created");
      setCreateDialogOpen(false);
      setNewEncounterName("");
      await fetchData();
      setActiveEncounter(res.data);
    } catch { toast.error("Failed to create encounter"); }
  };

  const deleteEncounter = async (id) => {
    const ok = await confirm("Delete Encounter", "Delete this encounter?");
    if (!ok) return;
    try {
      await axios.delete(`${API}/encounters/${id}`, { withCredentials: true });
      toast.success("Encounter deleted");
      if (activeEncounter?.id === id) { setActiveEncounter(null); setSelectedCombatant(null); }
      fetchData();
    } catch { toast.error("Failed to delete encounter"); }
  };

  const addCombatant = async () => {
    if (!activeEncounter || !newCombatant.name.trim()) return;
    try {
      await axios.post(`${API}/encounters/${activeEncounter.id}/combatants`, newCombatant, { withCredentials: true });
      toast.success(`${newCombatant.name} added`);
      setAddDialogOpen(false);
      setNewCombatant({ name: "", initiative: 10, hp: 10, max_hp: 10, ac: 10, is_player: false, source_id: "", source_type: "" });
      refreshEncounter(activeEncounter.id);
    } catch { toast.error("Failed to add combatant"); }
  };

  const addFromEntity = (entity, type) => {
    const isPlayer = type === "character" && entity.character_type === "pc";
    const hp = parseFirstNumber(entity.hit_points, 10);
    const ac = parseFirstNumber(entity.armor_class, 10);
    const init = parseFirstNumber(entity.initiative, 10);
    setNewCombatant({ name: entity.name, initiative: init, hp, max_hp: hp, ac, is_player: isPlayer, source_id: entity.id, source_type: type });
    setAddDialogOpen(true);
  };

  const removeCombatant = async (combatantId) => {
    if (!activeEncounter) return;
    try {
      await axios.delete(`${API}/encounters/${activeEncounter.id}/combatants/${combatantId}`, { withCredentials: true });
      if (selectedCombatant?.id === combatantId) { setSelectedCombatant(null); setSelectedEntity(null); }
      refreshEncounter(activeEncounter.id);
    } catch { toast.error("Failed to remove"); }
  };

  const applyDamage = async (combatantId) => {
    const val = parseInt(dmgInputs[combatantId]);
    if (isNaN(val) || val === 0) return;
    try {
      await axios.put(`${API}/encounters/${activeEncounter.id}/combatants/${combatantId}/hp`, {
        hp_change: -Math.abs(val)
      }, { withCredentials: true });
      setDmgInputs(prev => ({ ...prev, [combatantId]: "" }));
      refreshEncounter(activeEncounter.id);
    } catch { toast.error("Failed to apply damage"); }
  };

  const applyHeal = async (combatantId) => {
    const val = parseInt(dmgInputs[combatantId]);
    if (isNaN(val) || val === 0) return;
    try {
      await axios.put(`${API}/encounters/${activeEncounter.id}/combatants/${combatantId}/hp`, {
        hp_change: Math.abs(val)
      }, { withCredentials: true });
      setDmgInputs(prev => ({ ...prev, [combatantId]: "" }));
      refreshEncounter(activeEncounter.id);
    } catch { toast.error("Failed to heal"); }
  };

  const updateInitiative = async (combatantId, init) => {
    if (!activeEncounter) return;
    try {
      await axios.put(`${API}/encounters/${activeEncounter.id}/combatants/${combatantId}/initiative`, {
        initiative: init
      }, { withCredentials: true });
      refreshEncounter(activeEncounter.id);
    } catch { toast.error("Failed to update initiative"); }
  };

  const toggleCondition = async (combatantId, condition, currentConditions) => {
    if (!activeEncounter) return;
    const updated = currentConditions.includes(condition)
      ? currentConditions.filter(c => c !== condition)
      : [...currentConditions, condition];
    try {
      await axios.put(`${API}/encounters/${activeEncounter.id}/combatants/${combatantId}/conditions`, {
        conditions: updated
      }, { withCredentials: true });
      refreshEncounter(activeEncounter.id);
    } catch { toast.error("Failed to update conditions"); }
  };

  const nextTurn = async () => {
    if (!activeEncounter) return;
    try {
      const res = await axios.post(`${API}/encounters/${activeEncounter.id}/next-turn`, {}, { withCredentials: true });
      setActiveEncounter(res.data);
      setEncounters(prev => prev.map(e => e.id === res.data.id ? res.data : e));
    } catch { toast.error("Failed to advance turn"); }
  };

  const endEncounter = async () => {
    if (!activeEncounter) return;
    try {
      await axios.post(`${API}/encounters/${activeEncounter.id}/end`, {}, { withCredentials: true });
      toast.success("Encounter ended");
      refreshEncounter(activeEncounter.id);
    } catch { toast.error("Failed to end encounter"); }
  };

  const selectCombatant = useCallback((combatant) => {
    setSelectedCombatant(combatant);
    if (combatant.source_id && combatant.source_type) {
      let entity = null;
      if (combatant.source_type === "creature") entity = creatures.find(c => c.id === combatant.source_id);
      else if (combatant.source_type === "character") entity = characters.find(c => c.id === combatant.source_id);
      else if (combatant.source_type === "npc") entity = npcs.find(n => n.id === combatant.source_id);
      setSelectedEntity(entity);
      setSelectedEntityType(combatant.source_type);
    } else {
      setSelectedEntity(null);
      setSelectedEntityType("");
    }
  }, [creatures, characters, npcs]);

  const sortedCombatants = activeEncounter?.combatants
    ? [...activeEncounter.combatants].sort((a, b) => b.initiative - a.initiative)
    : [];

  const filteredEntities = (list, type) => {
    if (!addSearch.trim()) return list;
    return list.filter(e => e.name.toLowerCase().includes(addSearch.toLowerCase()));
  };

  if (loading) {
    return <Layout campaignName="Loading..."><div className="flex justify-center py-20"><div className="spinner"></div></div></Layout>;
  }

  return (
    <Layout campaignName={campaign?.name}>
      <TierGate feature="Combat Tracker" description="Run combat encounters with initiative tracking, HP management, conditions, and stat blocks — D&D Beyond style.">
      <div data-testid="combat-tracker-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-serif text-foreground">Combat Tracker</h1>
            <p className="text-muted-foreground text-sm">Manage encounters in real-time</p>
          </div>
          <div className="flex gap-2">
            {/* Encounter Selector */}
            {encounters.length > 0 && (
              <select
                className="bg-input border border-border text-foreground text-sm rounded-sm px-2 py-1.5 h-9"
                value={activeEncounter?.id || ""}
                onChange={(e) => {
                  const enc = encounters.find(en => en.id === e.target.value);
                  setActiveEncounter(enc);
                  setSelectedCombatant(null);
                  setSelectedEntity(null);
                }}
                data-testid="encounter-selector"
              >
                {encounters.map(enc => (
                  <option key={enc.id} value={enc.id}>{enc.name}{!enc.is_active ? " (ended)" : ""}</option>
                ))}
              </select>
            )}
            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm h-9" data-testid="create-encounter-button">
                  <Plus className="w-4 h-4 mr-1" /> New Encounter
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border">
                <DialogHeader><DialogTitle className="font-serif text-xl">New Combat Encounter</DialogTitle></DialogHeader>
                <div className="space-y-4 mt-4">
                  <Input value={newEncounterName} onChange={(e) => setNewEncounterName(e.target.value)} placeholder="Goblin Ambush"
                    className="bg-input border-border rounded-sm" data-testid="encounter-name-input" onKeyDown={(e) => e.key === "Enter" && createEncounter()} />
                  <Button onClick={createEncounter} className="w-full bg-primary text-primary-foreground rounded-sm" data-testid="save-encounter-button">Create Encounter</Button>
                </div>
              </DialogContent>
            </Dialog>
            {activeEncounter && (
              <Button variant="ghost" size="icon" className="h-9 w-9 text-destructive" onClick={() => deleteEncounter(activeEncounter.id)} data-testid="delete-encounter-button">
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>

        {!activeEncounter ? (
          <div className="empty-state">
            <Swords className="empty-state-icon text-muted-foreground" />
            <h3 className="text-xl font-serif text-foreground mb-2">No encounters</h3>
            <p className="text-muted-foreground mb-6">Create a combat encounter to begin tracking</p>
          </div>
        ) : (
          <>
            {/* Encounter Controls */}
            {activeEncounter.is_active && (
              <div className="flex items-center gap-3 mb-4 p-3 bg-muted/20 border border-border rounded-sm">
                <span className="text-sm font-medium text-primary" data-testid="round-counter">Round {activeEncounter.current_round}</span>
                <span className="text-xs text-muted-foreground">{sortedCombatants.length} combatants</span>
                <div className="flex-1" />
                <Dialog open={addDialogOpen} onOpenChange={(v) => { setAddDialogOpen(v); if (!v) setAddSearch(""); }}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" className="border-border rounded-sm h-8" data-testid="add-combatant-button">
                      <UserPlus className="w-3.5 h-3.5 mr-1" /> Add
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-card border-border max-w-lg max-h-[80vh] overflow-y-auto">
                    <DialogHeader><DialogTitle className="font-serif text-xl">Add Combatant</DialogTitle></DialogHeader>
                    <div className="space-y-4 mt-4">
                      {(characters.length > 0 || creatures.length > 0 || npcs.length > 0) && (
                        <div className="space-y-3">
                          <Input value={addSearch} onChange={(e) => setAddSearch(e.target.value)} placeholder="Search characters, NPCs, creatures..."
                            className="bg-input border-border rounded-sm" data-testid="add-combatant-search" />
                          {filteredEntities(characters).length > 0 && (
                            <div>
                              <p className="text-[10px] text-muted-foreground mb-1 uppercase tracking-wider">Characters</p>
                              <div className="flex flex-wrap gap-1">
                                {filteredEntities(characters).map(c => (
                                  <Button key={c.id} variant="outline" size="sm" className="text-xs border-border h-7"
                                    onClick={() => addFromEntity(c, "character")} data-testid={`quick-add-char-${c.id}`}>
                                    <Users className="w-3 h-3 mr-1" />{c.name}
                                    <span className="ml-1 text-muted-foreground text-[10px]">HP:{parseFirstNumber(c.hit_points)} AC:{parseFirstNumber(c.armor_class)}</span>
                                  </Button>
                                ))}
                              </div>
                            </div>
                          )}
                          {filteredEntities(npcs).length > 0 && (
                            <div>
                              <p className="text-[10px] text-muted-foreground mb-1 uppercase tracking-wider">NPCs</p>
                              <div className="flex flex-wrap gap-1">
                                {filteredEntities(npcs).map(n => (
                                  <Button key={n.id} variant="outline" size="sm" className="text-xs border-border h-7"
                                    onClick={() => addFromEntity(n, "npc")} data-testid={`quick-add-npc-${n.id}`}>
                                    <Users className="w-3 h-3 mr-1" />{n.name}
                                    <span className="ml-1 text-muted-foreground text-[10px]">HP:{parseFirstNumber(n.hit_points)} AC:{parseFirstNumber(n.armor_class)}</span>
                                  </Button>
                                ))}
                              </div>
                            </div>
                          )}
                          {filteredEntities(creatures).length > 0 && (
                            <div>
                              <p className="text-[10px] text-muted-foreground mb-1 uppercase tracking-wider">Creatures</p>
                              <div className="flex flex-wrap gap-1">
                                {filteredEntities(creatures).map(c => (
                                  <Button key={c.id} variant="outline" size="sm" className="text-xs border-border h-7"
                                    onClick={() => addFromEntity(c, "creature")} data-testid={`quick-add-creature-${c.id}`}>
                                    <Skull className="w-3 h-3 mr-1" />{c.name}
                                    <span className="ml-1 text-muted-foreground text-[10px]">HP:{parseFirstNumber(c.hit_points)} AC:{parseFirstNumber(c.armor_class)}</span>
                                  </Button>
                                ))}
                              </div>
                            </div>
                          )}
                          <div className="border-t border-border my-2" />
                        </div>
                      )}
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">Or Create Custom</p>
                      <Input value={newCombatant.name} onChange={(e) => setNewCombatant({...newCombatant, name: e.target.value})}
                        placeholder="Combatant name" className="bg-input border-border rounded-sm" data-testid="combatant-name-input" />
                      <div className="grid grid-cols-4 gap-2">
                        <div><label className="text-xs text-muted-foreground">Init</label>
                          <Input type="number" value={newCombatant.initiative} onChange={(e) => setNewCombatant({...newCombatant, initiative: parseInt(e.target.value) || 0})}
                            className="bg-input border-border rounded-sm" data-testid="combatant-init-input" /></div>
                        <div><label className="text-xs text-muted-foreground">HP</label>
                          <Input type="number" value={newCombatant.hp} onChange={(e) => { const v = parseInt(e.target.value) || 1; setNewCombatant({...newCombatant, hp: v, max_hp: v}); }}
                            className="bg-input border-border rounded-sm" data-testid="combatant-hp-input" /></div>
                        <div><label className="text-xs text-muted-foreground">AC</label>
                          <Input type="number" value={newCombatant.ac} onChange={(e) => setNewCombatant({...newCombatant, ac: parseInt(e.target.value) || 10})}
                            className="bg-input border-border rounded-sm" data-testid="combatant-ac-input" /></div>
                        <div className="flex items-end">
                          <label className="flex items-center gap-1 text-xs text-muted-foreground cursor-pointer">
                            <input type="checkbox" checked={newCombatant.is_player} onChange={(e) => setNewCombatant({...newCombatant, is_player: e.target.checked})}
                              className="accent-primary" data-testid="combatant-is-player" /> PC
                          </label>
                        </div>
                      </div>
                      <Button onClick={addCombatant} className="w-full bg-primary text-primary-foreground rounded-sm" data-testid="confirm-add-combatant">Add to Battle</Button>
                    </div>
                  </DialogContent>
                </Dialog>
                <Button onClick={nextTurn} size="sm" className="bg-primary text-primary-foreground rounded-sm h-8" data-testid="next-turn-button">
                  <SkipForward className="w-3.5 h-3.5 mr-1" /> Next Turn
                </Button>
                <Button onClick={endEncounter} variant="outline" size="sm" className="border-destructive text-destructive rounded-sm h-8" data-testid="end-encounter-button">
                  <Flag className="w-3.5 h-3.5 mr-1" /> End
                </Button>
              </div>
            )}

            {/* ==================== D&D BEYOND STYLE SPLIT LAYOUT ==================== */}
            <div className="flex gap-4" style={{ height: "calc(100vh - 220px)" }}>
              {/* LEFT: Initiative Order */}
              <div className={`${selectedCombatant ? "w-1/2 lg:w-2/5" : "w-full"} overflow-y-auto space-y-1 pr-1 transition-all`} data-testid="initiative-list">
                {sortedCombatants.length === 0 ? (
                  <div className="text-center py-16 text-muted-foreground">
                    <UserPlus className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Add combatants to begin</p>
                  </div>
                ) : (
                  sortedCombatants.map((c, idx) => {
                    const isCurrentTurn = activeEncounter.is_active && idx === activeEncounter.current_turn;
                    const hpPercent = c.max_hp > 0 ? (c.hp / c.max_hp) * 100 : 0;
                    const hpColor = hpPercent > 50 ? "bg-green-600" : hpPercent > 25 ? "bg-yellow-600" : "bg-red-600";
                    const isDead = c.hp <= 0;
                    const isSelected = selectedCombatant?.id === c.id;

                    return (
                      <div
                        key={c.id}
                        className={`rounded-sm border transition-all cursor-pointer ${
                          isSelected ? "border-primary bg-primary/10" :
                          isCurrentTurn ? "border-primary/60 bg-primary/5" :
                          isDead ? "border-border/40 opacity-40" : "border-border hover:border-border/80"
                        }`}
                        onClick={() => selectCombatant(c)}
                        data-testid={`combatant-row-${c.id}`}
                      >
                        <div className="p-3">
                          {/* Top row: init, name, AC */}
                          <div className="flex items-center gap-2 mb-2">
                            {isCurrentTurn && <div className="w-2 h-2 rounded-full bg-primary animate-pulse flex-shrink-0" />}
                            <Input
                              type="number"
                              value={c.initiative}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => updateInitiative(c.id, parseInt(e.target.value) || 0)}
                              className="w-12 h-7 p-0 text-center bg-muted/30 border-border text-sm font-bold rounded-sm flex-shrink-0"
                              disabled={!activeEncounter.is_active}
                              data-testid={`init-input-${c.id}`}
                            />
                            <div className="flex-1 min-w-0">
                              <span className={`text-sm font-medium ${isDead ? "line-through text-muted-foreground" : c.is_player ? "text-primary" : "text-foreground"}`}>
                                {c.name}
                              </span>
                              {c.is_player && <span className="ml-1.5 text-[9px] bg-primary/20 text-primary px-1 py-0.5 rounded-sm">PC</span>}
                            </div>
                            <div className="flex items-center gap-1 text-xs text-muted-foreground flex-shrink-0">
                              <Shield className="w-3 h-3" />
                              <span className="font-medium">{c.ac}</span>
                            </div>
                            {activeEncounter.is_active && (
                              <button className="p-1 text-muted-foreground hover:text-destructive flex-shrink-0" onClick={(e) => { e.stopPropagation(); removeCombatant(c.id); }}
                                data-testid={`remove-combatant-${c.id}`}>
                                <X className="w-3 h-3" />
                              </button>
                            )}
                          </div>

                          {/* HP Bar */}
                          <div className="mb-2">
                            <div className="flex items-center justify-between text-xs mb-1">
                              <span className="flex items-center gap-1 text-muted-foreground">
                                <Heart className={`w-3 h-3 ${isDead ? "text-destructive" : "text-red-400"}`} /> HP
                              </span>
                              <span className={`font-bold tabular-nums ${isDead ? "text-destructive" : "text-foreground"}`}>
                                {c.hp} / {c.max_hp}
                              </span>
                            </div>
                            <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                              <div className={`h-full ${hpColor} transition-all duration-300`} style={{ width: `${Math.max(0, Math.min(100, hpPercent))}%` }} />
                            </div>
                          </div>

                          {/* Damage / Heal controls */}
                          {activeEncounter.is_active && (
                            <div className="flex gap-1.5 items-center" onClick={(e) => e.stopPropagation()}>
                              <Input
                                type="number"
                                min="0"
                                placeholder="0"
                                value={dmgInputs[c.id] || ""}
                                onChange={(e) => setDmgInputs(prev => ({...prev, [c.id]: e.target.value}))}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") applyDamage(c.id);
                                }}
                                className="w-16 h-7 text-xs text-center bg-input border-border rounded-sm p-1"
                                data-testid={`dmg-input-${c.id}`}
                              />
                              <Button size="sm" className="h-7 px-2.5 text-xs bg-red-700 hover:bg-red-600 text-white rounded-sm"
                                onClick={() => applyDamage(c.id)} data-testid={`damage-btn-${c.id}`}>
                                Damage
                              </Button>
                              <Button size="sm" className="h-7 px-2.5 text-xs bg-green-700 hover:bg-green-600 text-white rounded-sm"
                                onClick={() => applyHeal(c.id)} data-testid={`heal-btn-${c.id}`}>
                                Heal
                              </Button>
                            </div>
                          )}

                          {/* Conditions */}
                          {(c.conditions?.length > 0 || activeEncounter.is_active) && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {c.conditions?.map(cond => (
                                <span key={cond} className={`text-[9px] px-1.5 py-0.5 rounded-sm text-white ${CONDITION_COLORS[cond] || "bg-gray-600"}`}>
                                  {cond}
                                  {activeEncounter.is_active && (
                                    <button className="ml-0.5 opacity-70 hover:opacity-100" onClick={(e) => { e.stopPropagation(); toggleCondition(c.id, cond, c.conditions || []); }}>x</button>
                                  )}
                                </span>
                              ))}
                              {activeEncounter.is_active && (
                                <div className="relative">
                                  <button className="text-[9px] px-1.5 py-0.5 rounded-sm border border-dashed border-muted-foreground text-muted-foreground hover:border-primary hover:text-primary"
                                    onClick={(e) => { e.stopPropagation(); setConditionDropdown(conditionDropdown === c.id ? null : c.id); }}
                                    data-testid={`add-condition-${c.id}`}>+</button>
                                  {conditionDropdown === c.id && (
                                    <div className="absolute top-6 left-0 z-50 bg-card border border-border rounded-sm shadow-lg p-1 min-w-[140px] max-h-48 overflow-y-auto">
                                      {CONDITIONS.filter(cn => !c.conditions?.includes(cn)).map(cond => (
                                        <button key={cond} className="block w-full text-left text-xs px-2 py-1 hover:bg-muted/50 text-foreground rounded-sm"
                                          onClick={(e) => { e.stopPropagation(); toggleCondition(c.id, cond, c.conditions || []); setConditionDropdown(null); }}>
                                          {cond}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* RIGHT: Stat Sheet Panel (side by side) */}
              {selectedCombatant && (
                <div className="w-1/2 lg:w-3/5 border-l border-border pl-4 overflow-y-auto" data-testid="stat-sheet-panel">
                  <div className="flex items-center justify-between mb-4 sticky top-0 bg-background py-2 z-10 border-b border-border/50 pb-3">
                    <div>
                      <h2 className="text-lg font-serif text-foreground">{selectedCombatant.name}</h2>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                        <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> AC {selectedCombatant.ac}</span>
                        <span className="flex items-center gap-1"><Heart className="w-3 h-3" /> {selectedCombatant.hp}/{selectedCombatant.max_hp} HP</span>
                        <span>Init {selectedCombatant.initiative}</span>
                      </div>
                    </div>
                    <button onClick={() => { setSelectedCombatant(null); setSelectedEntity(null); }}
                      className="p-1.5 text-muted-foreground hover:text-foreground transition-colors" data-testid="close-stat-panel">
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  {selectedEntity ? (
                    <>
                      {selectedEntityType === "creature" && <CreatureStatBlock entity={selectedEntity} />}
                      {selectedEntityType === "character" && <CharacterStatBlock entity={selectedEntity} />}
                      {selectedEntityType === "npc" && <NPCStatBlock entity={selectedEntity} />}
                    </>
                  ) : (
                    <div className="text-center py-12 text-muted-foreground">
                      <p className="text-sm">No linked stat block for this combatant.</p>
                      <p className="text-xs mt-1">Quick-add from your campaign entities to see full stats here.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
      </TierGate>
    </Layout>
  );
};

/* ====================== STAT BLOCK COMPONENTS ====================== */

const StatRow = ({ label, value }) => {
  if (!value) return null;
  return (
    <div className="flex justify-between py-1.5 text-sm border-b border-border/20">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-foreground font-medium text-right max-w-[60%]">{value}</span>
    </div>
  );
};

const ActionBlock = ({ title, actions, icon }) => {
  if (!actions || actions.length === 0) return null;
  return (
    <div className="mt-4">
      <h4 className="text-xs font-semibold text-primary flex items-center gap-1.5 mb-2 uppercase tracking-wider">
        {icon} {title}
      </h4>
      <div className="space-y-2">
        {actions.map((action, i) => (
          <div key={i} className="bg-muted/15 rounded-sm p-2.5 border border-border/20">
            <p className="text-sm font-medium text-foreground">{action.name}</p>
            <p className="text-xs text-foreground/80 mt-0.5 whitespace-pre-wrap">{action.description || action.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const AbilityScores = ({ entity }) => {
  const abilities = [
    { key: "str_score", label: "STR" }, { key: "dex_score", label: "DEX" },
    { key: "con_score", label: "CON" }, { key: "int_score", label: "INT" },
    { key: "wis_score", label: "WIS" }, { key: "cha_score", label: "CHA" },
  ];
  const hasAny = abilities.some(a => entity[a.key] && entity[a.key] !== 10);
  if (!hasAny) return null;
  return (
    <div className="grid grid-cols-6 gap-2 my-3">
      {abilities.map(a => (
        <div key={a.key} className="text-center bg-muted/20 rounded-sm py-2 border border-border/20">
          <p className="text-[10px] text-muted-foreground uppercase">{a.label}</p>
          <p className="text-lg font-bold text-foreground">{entity[a.key] || 10}</p>
          <p className="text-[10px] text-primary">{abilityMod(entity[a.key] || 10)}</p>
        </div>
      ))}
    </div>
  );
};

const CreatureStatBlock = ({ entity }) => (
  <div className="space-y-1" data-testid="creature-stat-block">
    <div className="flex flex-wrap gap-1.5 mb-3">
      {entity.size && <Badge variant="outline" className="text-[10px] border-border">{entity.size}</Badge>}
      {entity.creature_type && <Badge variant="outline" className="text-[10px] border-border">{entity.creature_type}</Badge>}
      {entity.alignment && <Badge variant="outline" className="text-[10px] border-border">{entity.alignment}</Badge>}
      {entity.challenge_rating && <Badge className="bg-red-900/40 text-red-300 text-[10px] border-none">CR {entity.challenge_rating}</Badge>}
    </div>
    <StatRow label="Armor Class" value={entity.armor_class} />
    <StatRow label="Hit Points" value={entity.hit_points} />
    <StatRow label="Speed" value={entity.speed} />
    <AbilityScores entity={entity} />
    <StatRow label="Saving Throws" value={entity.saving_throws} />
    <StatRow label="Skills" value={entity.skills} />
    <StatRow label="Damage Resistances" value={entity.damage_resistances} />
    <StatRow label="Damage Immunities" value={entity.damage_immunities} />
    <StatRow label="Condition Immunities" value={entity.condition_immunities} />
    <StatRow label="Senses" value={entity.senses} />
    <StatRow label="Languages" value={entity.languages} />
    <ActionBlock title="Traits" actions={entity.traits} icon={<BookOpen className="w-3.5 h-3.5" />} />
    <ActionBlock title="Actions" actions={entity.actions} icon={<Swords className="w-3.5 h-3.5" />} />
    <ActionBlock title="Reactions" actions={entity.reactions} icon={<Zap className="w-3.5 h-3.5" />} />
    <ActionBlock title="Legendary Actions" actions={entity.legendary_actions} icon={<Target className="w-3.5 h-3.5" />} />
    {entity.legendary_description && <p className="text-xs text-foreground/70 mt-1 italic">{entity.legendary_description}</p>}
    {entity.description && (
      <div className="mt-4 pt-3 border-t border-border">
        <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Description</p>
        <div className="text-sm text-foreground/80 rich-content prose prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: entity.description }} />
      </div>
    )}
  </div>
);

const CharacterStatBlock = ({ entity }) => {
  const sheet = entity.character_sheet || {};
  return (
    <div className="space-y-1" data-testid="character-stat-block">
      <div className="flex flex-wrap gap-1.5 mb-3">
        {entity.race && <Badge variant="outline" className="text-[10px] border-border">{entity.race}</Badge>}
        {entity.character_class && <Badge variant="outline" className="text-[10px] border-border">{entity.character_class}</Badge>}
        <Badge className="bg-primary/20 text-primary text-[10px] border-none">Lvl {entity.level || 1}</Badge>
      </div>
      <StatRow label="Armor Class" value={entity.armor_class} />
      <StatRow label="Hit Points" value={entity.hit_points} />
      <StatRow label="Speed" value={entity.speed} />
      <StatRow label="Initiative" value={entity.initiative} />
      <StatRow label="Player" value={entity.player_name} />
      {sheet.strength && (
        <div className="grid grid-cols-6 gap-2 my-3">
          {["strength", "dexterity", "constitution", "intelligence", "wisdom", "charisma"].map(ab => (
            <div key={ab} className="text-center bg-muted/20 rounded-sm py-2 border border-border/20">
              <p className="text-[10px] text-muted-foreground uppercase">{ab.slice(0, 3)}</p>
              <p className="text-lg font-bold text-foreground">{sheet[ab] || 10}</p>
              <p className="text-[10px] text-primary">{abilityMod(sheet[ab] || 10)}</p>
            </div>
          ))}
        </div>
      )}
      {entity.backstory && (
        <div className="mt-4 pt-3 border-t border-border">
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Backstory</p>
          <div className="text-sm text-foreground/80 rich-content prose prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: entity.backstory }} />
        </div>
      )}
    </div>
  );
};

const NPCStatBlock = ({ entity }) => (
  <div className="space-y-1" data-testid="npc-stat-block">
    <div className="flex flex-wrap gap-1.5 mb-3">
      {entity.race && <Badge variant="outline" className="text-[10px] border-border">{entity.race}</Badge>}
      {entity.profession && <Badge variant="outline" className="text-[10px] border-border">{entity.profession}</Badge>}
    </div>
    <StatRow label="Armor Class" value={entity.armor_class} />
    <StatRow label="Hit Points" value={entity.hit_points} />
    <StatRow label="Speed" value={entity.speed} />
    <StatRow label="Personality" value={entity.personality} />
    {entity.description && (
      <div className="mt-4 pt-3 border-t border-border">
        <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Description</p>
        <div className="text-sm text-foreground/80 rich-content prose prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: entity.description }} />
      </div>
    )}
  </div>
);

export default CombatTrackerPage;
